/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CustomTextarea,
  InputField,
  SelectField,
  TextareaField,
} from "@/components";
import { Breadcrumb, Button, Card, Dialog } from "tvs-unified-lib";
import {
  Body,
  ButtonContainer,
  CardDetail,
  ChipGroup,
  DialogActions,
  DialogMessage,
  LayoutLeft,
  LayoutRight,
  Root,
  SubTextDetail,
  TextDetail,
  Title,
} from "./styles";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatDateTime } from "@/utils/common";
import { channelFormOptions, optionsDataSource } from "../helpers";
import HierarchicalMultiSelect, {
  Option,
} from "@/components/HierarchicalMultiSelect";
import { preDefined, schema, TemplateForm } from "./helpers";
import Chip from "@/components/Chip";
import usePortfolioCategory from "@/services/hooks/portfolio/usePortfolio";
import { PortfolioItem, PortfolioItemChild } from "@/services/network/type";
import {
  useTemplateCreate,
  useTemplateDetail,
  useTemplateUpdate,
} from "@/services/hooks/template/useTemplate";
import useAuthStore from "@/store/authStore";
import { useSnackbar } from "notistack";
import { uploadApi } from "@/services";

export const TemplateEditCreateForm = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const { data: portfolioCategory } = usePortfolioCategory();
  const { data: templateDetail, refetch: refetchTemplateDetail } =
    useTemplateDetail(id || "");
  const { mutation: createTemplate, isPending: isCreating } =
    useTemplateCreate();
  const { mutation: updateTemplate, isPending: isUpdating } =
    useTemplateUpdate();

  const [portfolioOptions, setPortfolioOptions] = useState<Option[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const messageTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { control, handleSubmit, reset, getValues, setValue, watch } =
    useForm<TemplateForm>({
      resolver: yupResolver(schema) as any,
      mode: "all",
    });

  const checkDataUpload =
    watch("data_source")?.length &&
    watch("data_source")[0].value === "Data Upload";
  const checkDataAIDC =
    watch("data_source")?.length &&
    watch("data_source")[0].value === "AIDC";
  const dataFile = watch("data_file_id");

  const preDefinedUpload = useMemo(() => {
    if (checkDataUpload && dataFile?.length) {
      return dataFile[0].other;
    }
    if (checkDataAIDC) {
      return preDefined;
    }
    return [];
  }, [checkDataUpload, dataFile, checkDataAIDC]);

  useEffect(() => {
    if (portfolioCategory) {
      setPortfolioOptions(
        portfolioCategory.map((item: PortfolioItem) => ({
          label: item.name,
          value: item.name,
          name: item.name,
          children: item.items.map((child: PortfolioItemChild) => ({
            label: child.value,
            value: child.id,
            name: child.name,
          })),
        }))
      );
    }
  }, [portfolioCategory]);

  useEffect(() => {
    if (templateDetail) {
      const portfolio = templateDetail?.campaignTemplatePortfolioCategories;
      const channel = channelFormOptions.find(
        (item) => item.value === templateDetail?.channel
      );
      const dataSource = optionsDataSource.find(
        (item) => item.value === templateDetail?.dataSource
      );

      const dataFile = templateDetail?.dataFile;

      reset({
        ...templateDetail,
        channel: channel ? [channel] : [],
        data_source: dataSource ? [dataSource] : [],

        ...(portfolio?.length && {
          portfolio_category_id: portfolio.map((item) => ({
            label: item?.portfolioCategory?.value,
            name: item?.portfolioCategory?.name,
            value: item?.portfolioCategory?.id,
          })),
        }),
        ...(dataFile && {
          data_file_id: [
            {
              label: dataFile.fileName,
              value: dataFile.id,
              other: dataFile.excelHeaders,
            },
          ],
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateDetail]);

  useEffect(() => {
    if (!id) {
      reset({
        createdAt: new Date().toISOString(),
        createdUser: {
          firstName: user?.first_name || "",
          lastName: user?.last_name || "",
        },
      });
    } else {
      refetchTemplateDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!checkDataUpload || !dataFile?.length)
      setValue("data_file_id", undefined);
    if (!watch("portfolio_category_id")?.length) {
      setValue("portfolio_category_id", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDataUpload, dataFile, watch("portfolio_category_id")]);

  const onSubmit = async (data: TemplateForm) => {
    if (isCreating || isUpdating) return;
    const body = {
      description: data.description || "",
      name: data.name,
      message: data.message || "",
      createdAt: data.createdAt,
      portfolio_category_id: data.portfolio_category_id[0].value,
      portfolios: data.portfolio_category_id.map((item) => item.value),
      channel: data.channel?.[0]?.value,
      data_source: data.data_source?.[0]?.value,
      ...(checkDataUpload && {
        data_file_id: data.data_file_id?.[0]?.value,
      }),
    };
    try {
      if (id) {
        await updateTemplate.mutateAsync({ id, data: body });
      } else {
        await createTemplate.mutateAsync(body);
      }
      enqueueSnackbar(
        id ? "Template edited successfully." : "Template added successfully",
        {
          variant: "success",
        }
      );
      navigate("/templates");
    } catch (e) {
      enqueueSnackbar(e, {
        variant: "error",
      });
    }
  };

  const formatParameterTag = (label: string): string => `<${label}>`;

  const handleChipClick = (parameterLabel: string) => {
    const textarea = messageTextareaRef.current;

    if (!textarea) return;

    const currentMessage = getValues("message") || "";
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const textToInsert = formatParameterTag(parameterLabel);

    const newMessage =
      currentMessage.substring(0, selectionStart) +
      textToInsert +
      currentMessage.substring(selectionEnd);

    setValue("message", newMessage, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    requestAnimationFrame(() => {
      if (messageTextareaRef.current) {
        const newCursorPosition = selectionStart + textToInsert.length;
        messageTextareaRef.current.selectionStart = newCursorPosition;
        messageTextareaRef.current.selectionEnd = newCursorPosition;
        messageTextareaRef.current.focus();
      }
    });
  };

  const fetchOptionsDataFile = async (_, page: number = 1) => {
    return uploadApi
      .list({
        limit: 10,
        page: page,
        status: "success",
      })
      .then(({ data }) => ({
        data:
          data?.response?.map((item) => ({
            label: item.fileName,
            value: item.id,
            other: item.excelHeaders,
          })) || [],
        totalPages: Math.ceil(data?.totalCount / 10) || 0,
      }))
      .catch(() => {
        return { data: [], totalPages: 0 };
      });
  };

  return (
    <Root onSubmit={handleSubmit(onSubmit)}>
      <Breadcrumb
        items={[
          { content: <a href="/templates">Templates</a> },
          {
            content: <a href="#">{id ? `Edit` : `Add`} Template</a>,
            isActive: true,
          },
        ]}
      />
      <Title>{id ? `Edit` : `Add`} Template</Title>
      <Body>
        <LayoutLeft>
          <Card heading="Detail" className="card-detail">
            <CardDetail>
              <TextDetail>Created By</TextDetail>
              <SubTextDetail>
                {getValues("createdUser.firstName")}{" "}
                {getValues("createdUser.lastName")}
              </SubTextDetail>
            </CardDetail>
            <CardDetail>
              <TextDetail>Created Date</TextDetail>
              <SubTextDetail>
                {formatDateTime(getValues("createdAt") || "-")}
              </SubTextDetail>
            </CardDetail>
          </Card>
          <Card heading="Communication Details" className="card-information">
            <InputField
              name="name"
              label="Template Name"
              placeholder="Enter Template Name"
              control={control}
              isRequired
              icon={null}
            />
            <SelectField
              name="channel"
              label="Channel"
              placeholder="Select Channel"
              control={control}
              options={channelFormOptions}
              isRequired
            />
            <SelectField
              name="data_source"
              label="Data Source"
              placeholder="Select Data Source"
              control={control}
              options={optionsDataSource}
              isRequired
            />
            {!!checkDataUpload && (
              <SelectField
                name="data_file_id"
                label="Data File"
                placeholder="Select Data File"
                control={control}
                fetchOptions={fetchOptionsDataFile}
                isRequired
              />
            )}
            <HierarchicalMultiSelect
              label="Portfolio"
              isRequired
              options={portfolioOptions}
              rootTitle="All Portfolio"
              placeholder="Select Portfolio"
              control={control}
              name="portfolio_category_id"
            />
            <TextareaField
              name="description"
              label="Description"
              placeholder="Enter Description"
              control={control}
              characterLimit={300}
            />
          </Card>
        </LayoutLeft>
        <LayoutRight>
          <Card heading="Message">
            <CustomTextarea
              name="message"
              label="Enter Message"
              placeholder="Type message"
              control={control}
              characterLimit={300}
              ref={messageTextareaRef}
            />
            <ChipGroup>
              {preDefinedUpload.map((item: string) => {
                return (
                  <Chip
                    key={`pre-defined-${item}`}
                    label={item}
                    variant="purple"
                    onClick={() => handleChipClick(item)}
                  />
                );
              })}
            </ChipGroup>
          </Card>
        </LayoutRight>
      </Body>
      <ButtonContainer>
        <Button
          label="Cancel"
          variant="outline"
          onClick={() => setOpenConfirmDialog(true)}
        />
        <Button
          disabled={isCreating || isUpdating}
          label="Save"
          type="submit"
        />
      </ButtonContainer>
      {openConfirmDialog && (
        <Dialog size="sm">
          <DialogMessage>Are you sure you want to cancel?</DialogMessage>
          <DialogActions>
            <Button
              label="No"
              variant="outline"
              size="md"
              onClick={() => setOpenConfirmDialog(false)}
            />
            <Button
              label="Yes"
              size="md"
              onClick={() => {
                navigate("/templates");
              }}
            />
          </DialogActions>
        </Dialog>
      )}
    </Root>
  );
};

export default TemplateEditCreateForm;
